import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { CallbackFn, TableItem } from "@/types";
import { floatFormatter, isBoolean, isDefined, isFunction, isUndefinedOrNull, toFloat } from "@/utils";
import ClearableInput from "@/components/ClearableInput.vue";

type ItemType = never;
type ItemValue = never;

export default defineComponent({
  components: { ClearableInput },
  props: {
    item: {
      type: Object as PropType<TableItem<ItemType, ItemValue>>,
      required: true,
    },
    row: {
      // primary key della riga
      type: [String, Number],
      required: true,
    },
  },
  methods: {
    isEnabled(item: TableItem<ItemType, ItemValue>): boolean {
      if (typeof item?.field?.enabled === "boolean") {
        return item.field.enabled as boolean;
      }
      if (typeof item?.field?.enabled === "function") {
        return item.field.enabled(item);
      }
      return true;
    },
    floatFormatter(value: string | number | null): string {
      return floatFormatter(value);
    },
    isBoolean(item: TableItem<ItemType, never | boolean>): boolean {
      return item?.value === false || item.value === true;
    },

    isEditable(item: TableItem<ItemType, ItemValue>): boolean {
      return !!item?.field?.editable;
    },

    isClickable(item: TableItem<ItemType, ItemValue>): boolean {
      return typeof item?.field?.button === "object";
    },

    hasHelpText(item: TableItem<ItemType, ItemValue>): boolean {
      return typeof item?.field?.helpText !== "undefined";
    },

    getHelpText(item: TableItem<ItemType, ItemValue>): string | null {
      if (typeof item?.field?.helpText === "string") {
        return item.field.helpText;
      } else if (typeof item?.field?.helpText === "function") {
        return item.field.helpText(item);
      } else {
        return null;
      }
    },
    getEditableStep(item: TableItem<ItemType, ItemValue>): number {
      if (isUndefinedOrNull(item.field?.editable?.step)) {
        return 1;
      }
      if (item.field.editable?.step && isFunction(item.field.editable?.step)) {
        const fn = item.field.editable.step as CallbackFn<ItemType, ItemValue, number>;
        return fn(item);
      }
      return toFloat(item.field.editable?.step, 1);
    },

    hasDisabledText(item: TableItem<ItemType, ItemValue>): boolean {
      return typeof item?.field?.disabledText !== "undefined";
    },

    getDisabledText(item: TableItem<ItemType, ItemValue>): string | null {
      if (typeof item?.field?.disabledText === "string") {
        return item.field.disabledText;
      } else if (typeof item?.field?.disabledText === "function") {
        return item.field.disabledText(item);
      } else {
        return null;
      }
    },

    onChange(item: TableItem<ItemType, ItemValue>, name: string, event: Event): void {
      const value = event.target ? (event.target as HTMLInputElement).value : "";
      console.log("[TableCell.ts] onChange", item, name, value);
      if (!item) return;
      if (typeof item?.field?.formatter === "function") {
        item.value = item.field.formatter(value, name, item.item as ItemType);
      }
      if (item?.field?.button) {
        console.log("[TableCell.ts] buttonOnClick", item, name);
        if (isFunction(item.field.button.onClick)) {
          item.field.button.onClick(item, this.row);
        } else {
          console.error("item.field.button.onClick is not a function");
        }
      }

      this.$emit("change", {
        row: this.row,
        name: name,
        value: item.value,
        item: item,
      });
    },

    onBlur(item: TableItem<ItemType, ItemValue>, name: string): void {
      console.log("[TableCell.ts] onBlur", item, name);
    },

    hasError(item: TableItem<ItemType, ItemValue>): boolean | string {
      if (typeof item?.field?.validation === "undefined") return false;
      const result = item?.field?.validation(item);
      if (typeof result === "boolean") {
        return !result;
      } else {
        return result;
      }
    },
    format(item: TableItem<ItemType, ItemValue>): string {
      if (item.field.formatter) {
        if (isFunction(item.field.formatter)) {
          return item.field.formatter(item.value, "", item.item);
        } else {
          return String(item.field.formatter);
        }
      } else {
        return String(item.value ?? "");
      }
    },
    hasErrorMessage(item: TableItem<ItemType, ItemValue>): boolean {
      const error = this.hasError(item);
      return typeof error === "string";
    },

    getErrorMessage(item: TableItem<ItemType, ItemValue>): string | null {
      const error = this.hasError(item);
      if (typeof error === "string") {
        return error;
      }
      return null;
    },

    buttonIsDisabled<T, V>(item: TableItem<T, V>) {
      const field = item.field;
      if (!isDefined(field.button?.disabled)) {
        return false;
      }
      if (isBoolean(field.button?.disabled)) {
        return field.button?.disabled;
      }
      if (isFunction(field.button?.disabled)) {
        return field.button?.disabled(item);
      }
      return false;
    },

    sv<T>(val: T | undefined, def: T): T {
      return val ?? def;
    },
    ge(event: EventTarget | null): HTMLTextAreaElement | null {
      return event as HTMLTextAreaElement;
    },
  },
  computed: {
    cdata(): TableItem<ItemType, ItemValue> {
      if (!this.item) return {} as TableItem<ItemType, ItemValue>;
      if (this.item?.field?.calculate) {
        const item = { ...this.item };
        item.value = this.item?.field.calculate(this.item);
        return item;
      }
      return this.item;
    },
    formattedValue: {
      get(): string {
        return this.format(this.cdata);
      },
      set(value: string) {
        this.cdata.value = value;
      }
    },
  },
});
